import { type BeamstarPeriodTotals, type BeamstarSession } from '@consigli/types';

import { counted, formatCount, formatTokens } from '../format';

export interface Tile {
  label: string;
  value: string;
  unit?: string;
  note?: string;
}

const turnShareTile = (label: string, count: number, totals: BeamstarPeriodTotals): Tile => ({
  label,
  value: formatCount(count),
  unit: totals.turns === 0 ? undefined : `${((count / totals.turns) * 100).toFixed(1)}%`,
});

/** A turn with no ending an hour after it started never finished, so it counts as failed. */
const failedTurns = (totals: BeamstarPeriodTotals) => totals.failedTurns + totals.unfinishedTurns;

export const usageTiles = (totals: BeamstarPeriodTotals): Tile[] => [
  { label: 'Sessions', value: formatCount(totals.sessions) },
  { label: 'Turns', value: formatCount(totals.turns) },
  {
    label: 'Input tokens',
    value: formatTokens(totals.inputTokens),
    unit:
      totals.inputTokens === 0
        ? undefined
        : `${Math.round((totals.cachedInputTokens / totals.inputTokens) * 100)}% cached`,
  },
  { label: 'Output tokens', value: formatTokens(totals.outputTokens) },
];

/** Averages over the range's projects; a session with no project is left out. */
export const projectTiles = (sessions: BeamstarSession[]): Tile[] => {
  const inProjects = sessions.filter((session) => session.projectId !== null);
  const projects = new Set(inProjects.map((session) => session.projectId)).size;
  const withoutProject = sessions.length - inProjects.length;
  const leftOut =
    withoutProject === 0
      ? undefined
      : `Leaves out ${counted(withoutProject, 'session', 'sessions')} with no project`;
  if (projects === 0) {
    return [
      { label: 'Tokens per project', value: '-' },
      { label: 'Sessions per project', value: '-', note: leftOut },
    ];
  }
  const perProject = (value: (session: BeamstarSession) => number) =>
    inProjects.reduce((sum, session) => sum + value(session), 0) / projects;
  return [
    {
      label: 'Tokens per project',
      value: formatTokens(perProject((session) => session.inputTokens)),
      unit: `in · ${formatTokens(perProject((session) => session.outputTokens))} out`,
      note: counted(projects, 'project', 'projects'),
    },
    {
      label: 'Sessions per project',
      value: (inProjects.length / projects).toFixed(1),
      note: leftOut,
    },
  ];
};

export const qualityTiles = (totals: BeamstarPeriodTotals): Tile[] => [
  {
    ...turnShareTile('Failed turns', failedTurns(totals), totals),
    note:
      totals.unfinishedTurns === 0
        ? undefined
        : `${formatCount(totals.unfinishedTurns)} never finished`,
  },
  turnShareTile('Connection lost', totals.lostTurns, totals),
  turnShareTile('Stopped by user', totals.stoppedTurns, totals),
  { label: 'Failed tool calls', value: formatCount(totals.failedToolCalls) },
  { label: 'Flags', value: formatCount(totals.flags) },
];
